import { useEffect, useRef, useState } from 'react';

import { deleteLocalStorage, getLocalStorage, setLocalStorage } from 'src/functions/localStorageHandler';
import { useFormResultController } from './useFormResultController';
import { usePopupController } from './usePopupController';

export interface EmailPopupCustomization {
  title?: string;
  description?: string;
  shownStorageId?: string;
}

interface PopupVisibilityProps {
  baseStorageKey: string;
  customization: EmailPopupCustomization;
}

interface PopupVisibilityReturn {
  isPopupVisible: boolean;
  handleClose: () => void;
}

const usePopupVisibility = ({ customization, baseStorageKey }: PopupVisibilityProps): PopupVisibilityReturn => {
  const [isPopupVisible, setIsPopupVisible] = useState(false);

  const shownStorageId = customization?.shownStorageId
    ? `${baseStorageKey}Shown@${customization?.shownStorageId}`
    : `${baseStorageKey}Shown`;

  const startedTimeStorageId = customization?.shownStorageId
    ? `${baseStorageKey}StartedTime@${customization?.shownStorageId}`
    : `${baseStorageKey}StartedTime`;

  const timeLeftStorageId = customization?.shownStorageId
    ? `${baseStorageKey}TimeLeft@${customization?.shownStorageId}`
    : `${baseStorageKey}TimeLeft`;

  const { isEmailSubscriptionSubmitted } = useFormResultController();
  const { isPaused } = usePopupController();

  const pauseCompensationRef = useRef(0);

  const shouldShowPopup = (): boolean => {
    const popupShown = getLocalStorage(shownStorageId);
    const popShownAsTime = parseInt(popupShown as string);

    if (isEmailSubscriptionSubmitted()) return false;

    if (isNaN(popShownAsTime)) return true;

    const timeDifferenceInHours = (new Date().getTime() - popShownAsTime) / 1000 / 60 / 60;

    return timeDifferenceInHours >= 72;
  };

  const handleStorageOnModalOpen = (): void => {
    setLocalStorage(shownStorageId, new Date().getTime().toString());
    deleteLocalStorage(startedTimeStorageId);
    deleteLocalStorage(timeLeftStorageId);
  };

  const closeModalOnMouseLeave = (event: MouseEvent) => {
    if (!shouldShowPopup()) return;

    if (
      event.clientY <= 0 ||
      event.clientX <= 0 ||
      event.clientX >= window.innerWidth ||
      event.clientY >= window.innerHeight
    ) {
      handleOpen();
      handleStorageOnModalOpen();
    }
  };

  const handleOpen = (): void => {
    setIsPopupVisible(true);
  };

  const handleClose = (): void => {
    setIsPopupVisible(false);
  };

  const addCompensation = (): void => {
    pauseCompensationRef.current = 10000;
  };

  useEffect(() => {
    if (typeof window === 'undefined') return;

    if (!shouldShowPopup()) return;

    if (isPaused) {
      addCompensation();
      return;
    }

    let timeLeft: string | number = getLocalStorage(timeLeftStorageId);
    let startedTime: string | number = getLocalStorage(startedTimeStorageId);
    const timeToShow = 1000 * 60;

    // First time setting up
    if (!timeLeft || !startedTime) {
      timeLeft = timeToShow;
      setLocalStorage(timeLeftStorageId, timeLeft.toString());

      startedTime = new Date().getTime();
      setLocalStorage(startedTimeStorageId, startedTime.toString());
    }

    // Includes compensation for the time the popup was paused
    const timeLeftForTimer = Math.max(parseInt(timeLeft as string), 0) + pauseCompensationRef.current;

    const timer = setTimeout(() => {
      // prevent showing popup if already shown by mouseleave
      if (!shouldShowPopup()) {
        return;
      }

      handleOpen();
      handleStorageOnModalOpen();
    }, timeLeftForTimer);

    return () => {
      // Prevent localStorage changes after render
      if (!shouldShowPopup()) {
        clearTimeout(timer);
        return;
      }

      const timeDifference = new Date().getTime() - parseInt(startedTime as string);

      setLocalStorage(timeLeftStorageId, (timeToShow - timeDifference).toString());
      clearTimeout(timer);
    };
  }, [isPopupVisible, isPaused]);

  useEffect(() => {
    if (typeof window === 'undefined' || isPaused) return;

    document.addEventListener('mouseleave', closeModalOnMouseLeave);

    return () => {
      document.removeEventListener('mouseleave', closeModalOnMouseLeave);
    };
  }, [isPaused]);

  return { isPopupVisible, handleClose };
};

export default usePopupVisibility;
